/**
 * Lite inspect sonrası ağır tabloları arka planda Pinia'ya yükler.
 */
import api from '@/services/api.js'

const DEFAULT_HYDRATE = ['trips.txt', 'stop_times.txt']

/**
 * @param {object} gtfsStore
 * @param {File|Blob|null} file
 * @param {{ tables?: string[], includeShapes?: boolean }} [opts]
 */
export async function hydrateDeferredGtfsTables(gtfsStore, file, opts = {}) {
  if (!gtfsStore?.currentGTFSData || !file) {
    return { success: false, error: 'Feed veya dosya yok' }
  }

  const tables = [...(opts.tables || DEFAULT_HYDRATE)]
  if (opts.includeShapes) tables.push('shapes.txt')

  const pending = tables.filter((name) => {
    const key = name.endsWith('.txt') ? name : `${name}.txt`
    return Boolean(gtfsStore.currentGTFSData?.meta?.tables?.[key]?.rows_deferred)
  })

  if (!pending.length) {
    return { success: true, loaded: [], skipped: true }
  }

  gtfsStore.hydratingTables = true
  let cacheKey = gtfsStore.tableLoadCacheKey || null
  const loaded = []

  try {
    for (const tableName of pending) {
      const result = await api.loadGtfsTable({
        file: cacheKey ? null : file,
        cacheKey,
        tableName,
      })
      if (!result?.success) {
        throw new Error(result?.error || `${tableName} yüklenemedi`)
      }
      cacheKey = result.cache_key || cacheKey
      gtfsStore.tableLoadCacheKey = cacheKey || ''
      const short = String(result.table_name || tableName).replace(/\.txt$/, '')
      gtfsStore.setTableRows(short, result.rows || [], result.columns || [])
      loaded.push(short)
    }
    return { success: true, loaded, cacheKey }
  } catch (error) {
    return { success: false, error: error.message || String(error), loaded }
  } finally {
    gtfsStore.hydratingTables = false
  }
}

/** Export öncesi: deferred tablolar hâlâ boşsa orijinal zip'ten doldur */
export async function fillDeferredTablesFromZip(meta, originalFile) {
  if (!meta?.tables || !originalFile) return meta
  const deferred = Object.entries(meta.tables).filter(
    ([, t]) => t?.rows_deferred && !(t.sample_data?.length)
  )
  if (!deferred.length) return meta

  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(originalFile)
  for (const [name, table] of deferred) {
    const entry = zip.file(name)
    if (!entry) continue
    const text = await entry.async('string')
    const lines = text.split(/\r?\n/).filter((l) => l.trim())
    if (!lines.length) continue
    const columns = lines[0].split(',').map((c) => c.trim())
    const rows = []
    for (let i = 1; i < lines.length; i++) {
      const cols = parseCsvLine(lines[i])
      const row = {}
      columns.forEach((c, idx) => {
        row[c] = cols[idx] ?? ''
      })
      rows.push(row)
    }
    table.sample_data = rows
    table.record_count = rows.length
    table.columns = columns
    table.rows_deferred = false
  }
  return meta
}

function parseCsvLine(line) {
  const out = []
  let cur = ''
  let inQ = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"'
        i += 1
      } else {
        inQ = !inQ
      }
    } else if (ch === ',' && !inQ) {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out
}
